/**
 * A whole transcript, fetched a page at a time.
 *
 * **Imperative rather than a `useQuery` per page**, and for one reason: a meeting is thousands of
 * blocks, so a subscription per page would leave a reader holding tens of live subscriptions over
 * text that cannot change while they read it — a transcript is behind an activated revision, and
 * a revision is replaced whole rather than edited. One `convex.query` per page, appended as it
 * lands, is what that shape actually is.
 *
 * **The walk is pinned to the transcription the first page came from.** The first call names no
 * revision and is answered by the one the document reads as now; every continuation names that one
 * back, so a re-transcription landing mid-walk cannot splice its words into the transcript on
 * screen. A pinned revision whose blocks are gone refuses the continuation, and the walk restarts
 * from the top of whatever the document reads as now — visibly, because the reader is looking at
 * words that are about to be replaced.
 *
 * The pages are appended as they arrive rather than awaited as a set, because the first screenful
 * is the whole point: a person opens the transcript to read the top of it, and holding a blank
 * dialog until the last page of a five-hour recording has landed would be a spinner over text the
 * browser already has.
 */

import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { TranscriptSpeakerNames } from "@repo/shared";
import type { ConvexReactClient } from "convex/react";
import { useConvex } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { useEffect, useRef, useState } from "react";
import { getErrorMessage } from "@/lib/app-errors";
import { getAppErrorData } from "@/lib/convex-errors";
import {
  groupTranscriptTurns,
  type TranscriptBlock,
  type TranscriptDocument,
} from "../lib/transcript-turns-view";

type TranscriptPage = NonNullable<
  FunctionReturnType<typeof api.documentTranscript.listTranscriptBlocks>
>;

/**
 * A ceiling on the walk, so a runaway cursor cannot page forever.
 *
 * Well past the largest transcript the lane can produce: the worker refuses a recording longer
 * than five hours, and five hours of speech is low thousands of blocks.
 */
const MAX_TRANSCRIPT_PAGES = 250;

/** The refusal that means the pinned transcript is gone and the walk has to start again. */
const TRANSCRIPT_REVISION_GONE = "transcript_revision_gone";

/** Said once, where the words are, when a reload replaces the transcript being read. */
export const TRANSCRIPT_RELOADED_NOTE = "This transcript was updated — reloading.";

/** The transcript, as much of it as has been read so far. */
export type TranscriptDocumentState = {
  /** The display name the query resolved — the dialog's own header reads it. */
  name: string | null;
  /**
   * The name the file is *typed* from, which is what says whether the upload is audio or video.
   * The player needs it: a browser that cannot decode the derived Opus can fall back to an audio
   * upload's own bytes, and must never fall back to a gigabyte of video.
   */
  fileName: string | null;
  durationSeconds: number | null;
  /**
   * The BCP-47 codes language identification recorded, most-spoken first — empty where the run
   * identified none. The names a reader sees are resolved here rather than on the backend, which
   * is why these are codes.
   */
  languages: string[];
  /**
   * The names the recording arrived with — the first paint's labels, before the live
   * subscription in `useTranscriptSpeakerNames` has answered.
   */
  speakerNames: TranscriptSpeakerNames;
  /**
   * The transcription every page on screen came from. `null` until the first page lands; every
   * request about these words — the names, a rename, the audio behind them — names it.
   */
  processingRevisionId: string | null;
  /** Whether that transcription is still what the document reads as. */
  isActive: boolean;
  /**
   * The transcript being read was replaced mid-walk and the pages on screen are a fresh read of
   * whatever the document reads as now. `null` while nothing has been replaced.
   */
  reloadedNote: string | null;
  /**
   * The recording behind these words can be played.
   *
   * The backend's own answer — a succeeded transcription that kept the audio it derived — and the
   * only switch the viewer's player reads. `false` until the first page has landed, so nothing
   * offers a control before the transcript says there is something behind it.
   */
  playableMedia: boolean;
  /** Every turn grouped from the blocks loaded so far. */
  document: TranscriptDocument;
  /** How many of the revision's blocks have landed, and how many there are. */
  loadedBlockCount: number;
  blockCount: number | null;
  /** More pages are still coming. `false` with no turns means the transcript really is empty. */
  isLoading: boolean;
  /** The refusal, in the words the backend gave it. `null` while nothing has gone wrong. */
  error: string | null;
};

const EMPTY_DOCUMENT: TranscriptDocument = { note: null, turns: [], diarized: false };
const NO_LANGUAGES: string[] = [];
const NO_SPEAKER_NAMES: TranscriptSpeakerNames = {};

/**
 * Every page of one recording's transcript, in block order, from one transcription.
 *
 * Shared by the dialog (which shows the pages as they land) and the download (which needs the
 * last one before it can write a file), so the paging rule — the exclusive cursor, the pinned
 * revision, the ceiling, the stop on a null cursor — is stated once.
 *
 * A caller may name the transcription to read (an evidence citation opens the one it was made
 * against); with none, the walk takes whichever the document reads as now. Either way the first
 * page fixes the identity and every continuation carries it, so what is assembled is one
 * transcript rather than the front of one and the back of another.
 *
 * A pinned transcription that has gone away is answered by starting again, once, on the document
 * as it reads now — `onRestart` fires before the blocks already gathered are dropped, so a viewer
 * can say so rather than let the words change under the reader in silence.
 */
export async function fetchTranscriptDocument(
  convex: ConvexReactClient,
  args: {
    projectId: Id<"projects">;
    artifactId: Id<"artifacts">;
    /** The transcription to read. Absent reads the one the document reads as now. */
    processingRevisionId?: string;
  },
  onPage?: (page: TranscriptPage, blocks: readonly TranscriptBlock[]) => void,
  onRestart?: () => void
): Promise<{ page: TranscriptPage; blocks: TranscriptBlock[] } | null> {
  const scope = { projectId: args.projectId, artifactId: args.artifactId };
  let pinned = args.processingRevisionId;
  let restarted = false;
  let blocks: TranscriptBlock[] = [];
  let cursor: number | undefined;
  let last: TranscriptPage | null = null;

  for (let pageCount = 0; pageCount < MAX_TRANSCRIPT_PAGES; pageCount += 1) {
    let page: TranscriptPage | null;
    try {
      page = await convex.query(api.documentTranscript.listTranscriptBlocks, {
        ...scope,
        ...(cursor === undefined ? {} : { cursor }),
        ...(pinned === undefined
          ? {}
          : { processingRevisionId: pinned as Id<"artifactProcessingRevisions"> }),
      });
    } catch (error) {
      // The transcript being read is gone. Starting again is honest; carrying on into another
      // transcription's blocks is not. It happens once and is announced, and a second refusal is
      // a real failure.
      if (
        restarted ||
        pinned === undefined ||
        getAppErrorData(error)?.details?.reason !== TRANSCRIPT_REVISION_GONE
      ) {
        throw error;
      }
      restarted = true;
      pinned = undefined;
      blocks = [];
      cursor = undefined;
      last = null;
      onRestart?.();
      continue;
    }
    // The document or its project is gone — an ordinary state, and there is nothing to read.
    if (page === null) return null;
    pinned = page.processingRevisionId;
    blocks.push(...page.blocks);
    last = page;
    onPage?.(page, blocks);
    if (page.nextCursor === null) break;
    cursor = page.nextCursor;
  }

  return last === null ? null : { page: last, blocks };
}

/** Nothing read yet: the state a load starts from and the state a closed dialog holds. */
function emptyTranscriptState(isLoading: boolean): TranscriptDocumentState {
  return {
    name: null,
    fileName: null,
    durationSeconds: null,
    languages: NO_LANGUAGES,
    speakerNames: NO_SPEAKER_NAMES,
    processingRevisionId: null,
    isActive: false,
    reloadedNote: null,
    playableMedia: false,
    document: EMPTY_DOCUMENT,
    loadedBlockCount: 0,
    blockCount: null,
    isLoading,
    error: null,
  };
}

/**
 * Load one recording's transcript while `artifactId` names one, dropping a load whose document
 * has since been replaced.
 *
 * A generation ref rather than an abort: `convex.query` resolves whatever it was asked for, and
 * the only thing that matters is that a page for a transcript nobody is looking at any more never
 * reaches the state.
 *
 * `processingRevisionId` names the transcription to open — a citation's own, so the passage it
 * quotes is the passage on screen. Absent opens the one the document reads as now.
 */
export function useTranscriptDocument(
  projectId: Id<"projects">,
  artifactId: string | null,
  processingRevisionId?: string | null
): TranscriptDocumentState {
  const convex = useConvex();
  const [state, setState] = useState<TranscriptDocumentState>(() =>
    emptyTranscriptState(artifactId !== null)
  );
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    if (artifactId === null) {
      setState(emptyTranscriptState(false));
      return;
    }
    setState(emptyTranscriptState(true));

    void (async () => {
      // Set by the restart and read by every page after it, so the note outlives the moment the
      // words were replaced and stays with the transcript that replaced them.
      let reloadedNote: string | null = null;
      try {
        const result = await fetchTranscriptDocument(
          convex,
          {
            projectId,
            artifactId: artifactId as Id<"artifacts">,
            ...(processingRevisionId ? { processingRevisionId } : {}),
          },
          (page, blocks) => {
            if (generationRef.current !== generation) return;
            setState({
              name: page.name,
              fileName: page.fileName,
              durationSeconds: page.durationSeconds ?? null,
              languages: page.languages ?? NO_LANGUAGES,
              speakerNames: page.speakerNames,
              processingRevisionId: page.processingRevisionId,
              isActive: page.isActive === true,
              reloadedNote,
              playableMedia: page.playableMedia === true,
              document: groupTranscriptTurns(blocks),
              loadedBlockCount: blocks.length,
              blockCount: page.blockCount ?? null,
              isLoading: page.nextCursor !== null,
              error: null,
            });
          },
          () => {
            reloadedNote = TRANSCRIPT_RELOADED_NOTE;
            if (generationRef.current !== generation) return;
            // The pages on screen belong to a transcript that is gone: they go with it, and the
            // note stands in their place until the fresh read's first page lands.
            setState({ ...emptyTranscriptState(true), reloadedNote });
          }
        );
        if (generationRef.current !== generation) return;
        // A `null` result is a document that has gone away underneath the dialog; there is
        // nothing left to show and nothing that went wrong.
        if (result === null) setState((current) => ({ ...current, isLoading: false }));
      } catch (error) {
        if (generationRef.current !== generation) return;
        setState((current) => ({
          ...current,
          isLoading: false,
          error: getErrorMessage(error, "Could not load this transcript."),
        }));
      }
    })();
  }, [artifactId, convex, processingRevisionId, projectId]);

  return state;
}
